/**
 * WallSketch property hooks - REACTIVE VERSION
 * Provides reactive property access for skeleton skinning settings
 *
 * This uses the reactive property system which provides:
 * - Direct value access (property.value)
 * - Automatic updates to all subscribers (React + Potree)
 * - Automatic database persistence
 */

import { useCallback } from 'react';
import { PropertyType } from '../../types';
// Import directly to avoid pulling in problematic dependencies (breaks Potree build)
import { useReactivePropertyManager } from '../../reactive/useReactivePropertyManager';
import { ReactiveProperty } from '../../reactive/ReactiveProperty';
// Side-effect import ensures registration runs even if re-exports are tree-shaken
import './WallSketchPropertyGetters';

// Re-export everything from the getters file so existing client imports still work
export {
  registerWallSketchProperties,
  getWallSketchSkinning3DProperty,
  getWallSketchSkinning2DColorProperty,
  getWallSketchSkinning2DOutlineColorProperty,
  getWallSketchSDFEnabledProperty,
  getWallSketchSDFGradientProperty,
  getWallSketchSDFMaxDistanceProperty,
  getWallSketchSDFClipEnabledProperty,
  getWallSketchSDFClipMethodProperty,
  getWallSketchSDFClipToleranceProperty,
} from './WallSketchPropertyGetters';

// ============================================================================
// REACTIVE PROPERTY ACCESS HOOK
// ============================================================================

/**
 * Custom hook to get reactive skeleton properties
 * Returns ReactiveProperty instances that can be used directly
 *
 * @param projectId - The project identifier
 * @returns Object with reactive property getters
 */
export const useWallSketchProperties = (projectId: string) => {
  const service = useReactivePropertyManager({ projectId });

  /**
   * Get reactive property for skeleton skinning 3D mode
   */
  const getWallSketchSkinning3DPropertyHook = useCallback(
    (wallSketchId: string): ReactiveProperty<boolean> => {
      return service.getProperty('skeleton_skinning_3d', wallSketchId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<boolean>;
    },
    [service],
  );

  return {
    loading: service.loading,
    getWallSketchSkinning3DProperty: getWallSketchSkinning3DPropertyHook,
  };
};
